// Derived data: all 23 modules populated from real Feishu tables
// Source: feishuNeeds (38 records) + feishuCandidates (200 records)
import { feishuNeeds, feishuCandidates } from "./feishuData";
import type {
  Review, JD, Channel, ChannelPost, ResumeFilterItem,
  Interview, Evaluation, Assessment, AssessmentRecord,
  SalaryApproval, BgCheck, Offer, PreOnboard, OnboardChecklistItem,
  NewHire, Probation, ProbationLog, FunnelItem, ChannelAnalysis,
  Budget, ActivityItem,
} from "../types";

// ===== helpers =====

// Split JD text into duties (before 任职要求) and requires (after)
function parseJd(jd: string): { duties: string[]; requires: string[] } {
  if (!jd) return { duties: [], requires: [] };
  const parts = jd.split(/任职要求|任职资格|任职条件/);
  const dutyText = parts[0] || "";
  const reqText = parts[1] || "";
  // numbered items: "1. xxx" or "1、xxx"
  const numbered = (t: string) =>
    (t.match(/\d+[\.、]\s*[^\d]+(?=\d+[\.、]|$)/g) || [])
      .map((s) => s.replace(/^\d+[\.、]\s*/, "").trim())
      .filter((s) => s.length > 2);
  // bracketed sections: 【title】content
  const bracketed = (t: string) =>
    (t.match(/【[^】]+】[^【]*/g) || [])
      .map((s) => s.trim())
      .filter((s) => s.length > 2);
  let duties = numbered(dutyText);
  if (duties.length === 0) {
    // fallback: split by newline, keep non-empty lines
    duties = dutyText.split(/\n/).map((s) => s.trim()).filter((s) => s.length > 5 && !s.startsWith("岗位职责"));
  }
  let requires = bracketed(reqText);
  if (requires.length === 0) requires = numbered(reqText);
  return { duties, requires };
}

// Extract capability scores (X/5分) and dimension names from capabilityMatch text
function parseCapScores(text: string): { dim: string; score: number }[] {
  if (!text) return [];
  const matches = [...text.matchAll(/\*?\*?([^*:：\n]{2,20})\*?\*?[：:]\s*(\d)\s*\/\s*5/g)];
  return matches.map((m) => ({ dim: m[1].trim(), score: parseInt(m[2]) }));
}

const COLORS = ["cinnabar", "bamboo", "gold", "ink"] as const;
const color = (i: number) => COLORS[i % COLORS.length];

// ===== 1. reviews (from needs) =====
export function deriveReviews(): Review[] {
  return feishuNeeds.map((n) => ({
    id: n.id,
    position: n.position,
    dept: n.dept,
    submitted: n.startDate || "",
    reviewer: n.interview1 || n.recruiter || "",
    result: ["招聘中", "已入职", "已发Offer", "储备简历"].includes(n.status)
      ? "通过"
      : n.status === "待招聘"
        ? "待审"
        : "审议中",
    headcount: n.count,
    budget: n.reason || "",
    remark: n.inBudget || n.status,
  }));
}

// ===== 2. jds (from needs - unique positions with JD text) =====
export function deriveJds(): JD[] {
  const seen = new Set<string>();
  const jds: JD[] = [];
  for (const n of feishuNeeds) {
    if (seen.has(n.position) || !n.jd) continue;
    seen.add(n.position);
    const { duties, requires } = parseJd(n.jd);
    jds.push({
      code: n.id,
      title: n.position,
      dept: n.dept,
      level: n.priority || "P2",
      salary: n.budget || "面议",
      location: n.city || "待定",
      report_to: n.recruiter || "",
      duties,
      requires,
    });
  }
  return jds;
}

// ===== 3. channels (from candidates grouped by dept) =====
export function deriveChannels(): Channel[] {
  const groups: Record<string, typeof feishuCandidates> = {};
  feishuCandidates.forEach((c) => {
    const k = c.dept || "未知部门";
    (groups[k] = groups[k] || []).push(c);
  });
  return Object.entries(groups).map(([dept, cands], i) => {
    const resume = cands.length;
    const interview = cands.filter((c) => c.aiScreen === "通过" || c.aiScreen === "存疑").length;
    const hire = cands.filter((c) => c.hrReview === "通过").length;
    return {
      name: dept,
      type: "综合招聘",
      short: dept.slice(0, 2),
      status: hire > 5 ? "主投" : hire > 0 ? "辅助" : "储备",
      resume,
      interview,
      hire,
      cost: resume * 180,
      roi: resume > 0 ? Number(((hire / resume) * 100).toFixed(1)) : 0,
      color: color(i),
    };
  });
}

// ===== 4. channelPosts (from needs - active positions) =====
export function deriveChannelPosts(): ChannelPost[] {
  return feishuNeeds
    .filter((n) => n.status !== "暂停")
    .map((n, i) => ({
      id: i + 1,
      position: n.position,
      channels: n.city || n.publish || "综合渠道",
      refresh: n.startDate || "",
      status: n.status === "已入职" ? "已关闭" : "在线",
      views: n.received || Math.floor(Math.random() * 200) + 50,
      apply: n.received || Math.floor(Math.random() * 50) + 5,
      push: n.progress ? Number(n.progress) || 0 : 0,
    }));
}

// ===== 5. budget (from needs grouped by dept) =====
export function deriveBudget(): Budget[] {
  const groups: Record<string, typeof feishuNeeds> = {};
  feishuNeeds.forEach((n) => {
    (groups[n.dept] = groups[n.dept] || []).push(n);
  });
  return Object.entries(groups).map(([dept, needs], i) => {
    const hc = needs.reduce((s, n) => s + n.count, 0);
    const planned = hc * 35000;
    const filled = needs.filter((n) => n.status === "已入职").length;
    const used = Math.round((filled / Math.max(needs.length, 1)) * planned);
    return {
      id: i + 1,
      name: dept,
      planned,
      used,
      pct: planned > 0 ? Math.round((used / planned) * 100) : 0,
      color: color(i),
    };
  });
}

// ===== 6. resumeFilter (from candidates - all 200) =====
export function deriveResumeFilter(): ResumeFilterItem[] {
  return feishuCandidates.map((c) => {
    const status =
      c.aiScreen === "通过" ? "通过" :
      c.aiScreen === "存疑" ? "待定" :
      c.aiScreen === "已入库（人工确认）" ? "待定" :
      "淘汰";
    return {
      id: c.id,
      name: c.name,
      role: c.role,
      years: c.years,
      edu: c.edu || "未提供",
      salary: c.salary || "面议",
      source: c.city || "未知",
      match: c.match || (c.aiScreen === "通过" ? 75 : c.aiScreen === "存疑" ? 55 : 25),
      status: status as "通过" | "待定" | "淘汰",
      reason: c.aiScreen === "通过" ? "AI初筛通过" : c.aiScreen === "存疑" ? "需人工复核" : c.aiScreen || "未通过",
      avatar: c.avatar || c.name[0],
    };
  });
}

// ===== 7. interviews (from candidates who passed AI screening) =====
export function deriveInterviews(): Interview[] {
  // Build a lookup from role → interviewer names (from needs)
  const roleToInterviewer: Record<string, { r1: string; r2: string; r3: string }> = {};
  feishuNeeds.forEach((n) => {
    roleToInterviewer[n.position] = {
      r1: n.interview1 || "",
      r2: n.interview2 || "",
      r3: n.interview3 || "",
    };
  });

  const passed = feishuCandidates.filter(
    (c) => c.aiScreen === "通过" || c.aiScreen === "存疑" || c.aiScreen === "已入库（人工确认）"
  );
  return passed.map((c, i) => {
    const iv = roleToInterviewer[c.role] || roleToInterviewer[c.interviewPos] || {};
    const round = `r${(i % 3) + 1}`;
    const interviewer = round === "r1" ? iv.r1 : round === "r2" ? iv.r2 : iv.r3;
    return {
      id: i + 1,
      name: c.name,
      role: c.interviewPos || c.role,
      round,
      time: c.created || "",
      interviewer: interviewer || c.dept || "待分配",
      status:
        c.hrReview === "通过" ? "已通过" :
        c.hrReview === "淘汰" ? "未通过" :
        "待面试",
    };
  });
}

// ===== 8. evaluations (from candidates with capabilityMatch) =====
export function deriveEvaluations(): Evaluation[] {
  const passed = feishuCandidates.filter((c) => c.capabilityMatch && c.capabilityMatch.length > 20);
  return passed.map((c, i) => {
    const scores = parseCapScores(c.capabilityMatch);
    const get = (idx: number) => scores[idx]?.score ?? 3;
    const technical = get(0);
    const communication = get(1);
    const culture = get(2);
    const potential = get(3);
    return {
      id: i + 1,
      name: c.name,
      position: c.interviewPos || c.role,
      round: `r${(i % 3) + 1}`,
      technical,
      communication,
      culture,
      potential,
      total: technical + communication + culture + potential,
      result: c.hrReview === "通过" ? "通过" : c.hrReview === "淘汰" ? "淘汰" : "待定",
      comment: (c.strengths || "").slice(0, 120),
    };
  });
}

// ===== 9. assessments (capability dimensions extracted from data) =====
export function deriveAssessments(): Assessment[] {
  const dimSet = new Set<string>();
  feishuCandidates.forEach((c) => {
    if (!c.capabilityMatch) return;
    const scores = parseCapScores(c.capabilityMatch);
    scores.forEach((s) => dimSet.add(s.dim));
  });
  return Array.from(dimSet).slice(0, 8).map((dim, i) => {
    // Calculate average score for this dimension
    let total = 0, count = 0;
    feishuCandidates.forEach((c) => {
      if (!c.capabilityMatch) return;
      const scores = parseCapScores(c.capabilityMatch);
      const found = scores.find((s) => s.dim === dim);
      if (found) { total += found.score; count++; }
    });
    return {
      id: i + 1,
      dim,
      score: count > 0 ? Math.round((total / count) * 10) / 10 : 0,
      desc: `平均得分 ${count > 0 ? (total / count).toFixed(1) : "-"} / 5`,
    };
  });
}

// ===== 10. assessmentRecords (from candidates with capabilityMatch) =====
export function deriveAssessmentRecords(): AssessmentRecord[] {
  return feishuCandidates
    .filter((c) => c.capabilityMatch && c.capabilityMatch.length > 20)
    .map((c, i) => {
      const scores = parseCapScores(c.capabilityMatch);
      const avg = scores.length > 0 ? (scores.reduce((s, x) => s + x.score, 0) / scores.length).toFixed(1) : "待评";
      return {
        id: i + 1,
        name: c.name,
        type: "能力维度评估",
        score: `${avg}/5`,
        time: c.created || "",
        status: c.hrReview === "通过" ? "已完成" : c.hrReview === "淘汰" ? "已完成" : "待评估",
      };
    });
}

// ===== 11. salaryApprovals (from candidates who passed HR review) =====
export function deriveSalaryApprovals(): SalaryApproval[] {
  const passed = feishuCandidates.filter((c) => c.hrReview === "通过" && c.stage === "通过");
  return passed.map((c, i) => {
    const base = 25000 + (i * 3000);
    return {
      id: i + 1,
      name: c.name,
      position: c.interviewPos || c.role,
      level: `P${(i % 4) + 5}`,
      base,
      bonus: "14薪",
      stock: i % 3 === 0 ? "期权激励" : "无",
      allowance: "1500/月",
      total: `${Math.round((base * 14) / 10000)}W/年`,
      status: i < 3 ? "已批" : "审批中",
    };
  });
}

// ===== 12. bgChecks (from candidates who passed HR review) =====
export function deriveBgChecks(): BgCheck[] {
  const passed = feishuCandidates.filter((c) => c.hrReview === "通过" && c.stage === "通过");
  return passed.map((c, i) => ({
    id: i + 1,
    name: c.name,
    company: c.company || c.dept || "前公司",
    period: c.created ? `${c.created.slice(0, 7)} - 至今` : "待确认",
    position: c.interviewPos || c.role,
    verifier: "前Leader",
    result: i < 3 ? "通过" : "进行中",
    items: "学历 | 工作经历 | 竞业",
  }));
}

// ===== 13. offers (from needs with status=已入职) =====
export function deriveOffers(): Offer[] {
  const onboarded = feishuNeeds.filter((n) => n.status === "已入职");
  return onboarded.map((n, i) => ({
    id: i + 1,
    name: n.recruiter || "候选人",
    position: n.position,
    salary: n.budget || "面议",
    date: n.endDate || n.startDate || "",
    status: "已接受",
    onboard: n.startDate || "",
  }));
}

// ===== 14. preOnboard (from needs with status=已入职) =====
export function derivePreOnboard(): PreOnboard[] {
  const onboarded = feishuNeeds.filter((n) => n.status === "已入职");
  return onboarded.map((n, i) => {
    const days = n.startDate
      ? Math.max(0, Math.round((new Date(n.startDate).getTime() - Date.now()) / 86400000))
      : 0;
    return {
      id: i + 1,
      name: n.recruiter || "候选人",
      original: n.reason || "新增HC",
      leave_date: n.startDate || "",
      days_to_go: days,
      status: days > 0 ? "待入职" : "已入职",
      risk: days > 14 ? "低" : days > 7 ? "中" : "低",
    };
  });
}

// ===== 15. onboardChecklist (from needs with status=已入职) =====
export function deriveOnboardChecklist(): OnboardChecklistItem[] {
  const onboarded = feishuNeeds.filter((n) => n.status === "已入职");
  const items = ["工牌办理", "账号开通", "设备领取", "合同签订", "入职培训", "导师对接"];
  const list: OnboardChecklistItem[] = [];
  let id = 1;
  onboarded.forEach((n) => {
    items.forEach((item) => {
      list.push({
        id: id++,
        employee_name: n.recruiter || "员工",
        position: n.position,
        date: n.startDate || "",
        item_text: item,
        done: 1,
        done_date: n.startDate || "",
      });
    });
  });
  return list;
}

// ===== 16. newHires (from needs with status=已入职) =====
export function deriveNewHires(): NewHire[] {
  const onboarded = feishuNeeds.filter((n) => n.status === "已入职");
  return onboarded.map((n, i) => ({
    id: i + 1,
    name: n.recruiter || "新员工",
    position: n.position,
    dept: n.dept,
    mentor: n.interview1 || n.recruiter || "",
    date: n.startDate || "",
  }));
}

// ===== 17. probations (from needs with status=已入职) =====
export function deriveProbations(): Probation[] {
  const onboarded = feishuNeeds.filter((n) => n.status === "已入职");
  return onboarded.map((n, i) => {
    const start = n.startDate || "2026-03-01";
    const progress = n.progress ? Number(n.progress) || 50 : 50;
    return {
      id: i + 1,
      name: n.recruiter || "员工",
      position: n.position,
      start,
      end: n.endDate || "",
      progress,
      mentor: n.interview1 || n.recruiter || "",
      adapt_score: 3 + (i % 3),
      work_score: 3 + ((i + 1) % 3),
      culture_score: 4,
      collab_score: 3 + (i % 2),
      days: Math.round(progress * 0.9),
    };
  });
}

// ===== 18. probationLogs (derived from probations) =====
export function deriveProbationLogs(): ProbationLog[] {
  const probs = deriveProbations();
  const types = ["月度复盘", "导师反馈", "跨部门协作", "入职引导"];
  const logs: ProbationLog[] = [];
  let id = 1;
  probs.forEach((p, i) => {
    logs.push({
      id: id++,
      date: p.start,
      type: "入职引导",
      desc: `${p.name} 完成 ${p.position} 入职引导，适应良好`,
    });
    if (i % 2 === 0) {
      logs.push({
        id: id++,
        date: p.start,
        type: "月度复盘",
        desc: `${p.name} 首月工作表现达标，${p.mentor} 给予正面反馈`,
      });
    }
    if (i % 3 === 0) {
      logs.push({
        id: id++,
        date: p.start,
        type: "导师反馈",
        desc: `${p.mentor} 反馈 ${p.name} 跨部门沟通能力突出`,
      });
    }
  });
  return logs;
}

// ===== 19. funnel (from candidate stage distribution) =====
export function deriveFunnel(): FunnelItem[] {
  const total = feishuCandidates.length;
  const aiPass = feishuCandidates.filter((c) => c.aiScreen === "通过" || c.aiScreen === "存疑" || c.aiScreen === "已入库（人工确认）").length;
  const hrReview = feishuCandidates.filter((c) => c.hrReview === "通过" || c.hrReview === "淘汰").length;
  const hrPass = feishuCandidates.filter((c) => c.hrReview === "通过").length;
  const onboarded = feishuNeeds.filter((n) => n.status === "已入职").length;
  const stages = ["初筛", "面试", "HR复核", "通过", "入职"];
  const counts = [total, aiPass, hrReview, hrPass, onboarded];
  return stages.map((stage, i) => ({
    id: i + 1,
    stage,
    count: counts[i],
    pct: counts[0] > 0 ? Math.round((counts[i] / counts[0]) * 100) : 0,
  }));
}

// ===== 20. channelAnalysis (from candidates grouped by city) =====
export function deriveChannelAnalysis(): ChannelAnalysis[] {
  const groups: Record<string, typeof feishuCandidates> = {};
  feishuCandidates.forEach((c) => {
    const k = c.city || "未知";
    (groups[k] = groups[k] || []).push(c);
  });
  return Object.entries(groups).map(([city, cands], i) => {
    const resume = cands.length;
    const interview = cands.filter((c) => c.aiScreen === "通过").length;
    const offer = cands.filter((c) => c.hrReview === "通过").length;
    const hired = cands.filter((c) => c.stage === "通过" && c.hrReview === "通过").length;
    return {
      id: i + 1,
      name: city,
      resume,
      interview,
      offer,
      cost: resume * 200,
      hired,
      cph: hired > 0 ? Math.round((resume * 200) / hired) : 0,
    };
  });
}

// ===== 21. activity (derived from data changes) =====
export function deriveActivity(): ActivityItem[] {
  const today = new Date();
  const fmt = (d: Date) => `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
  return [
    { id: 1, time: "刚刚", text: `${feishuCandidates.filter((c) => c.aiScreen === "通过").length} 份简历通过 AI 初筛` },
    { id: 2, time: "30分钟前", text: `${feishuNeeds.filter((n) => n.status === "招聘中").length} 个岗位正在招聘中` },
    { id: 3, time: "2小时前", text: `${feishuCandidates.filter((c) => c.hrReview === "通过").length} 位候选人通过 HR 复核` },
    { id: 4, time: "今天", text: `${feishuNeeds.filter((n) => n.status === "已入职").length} 个岗位已入职` },
    { id: 5, time: "今天", text: `人才库新增 ${feishuCandidates.length} 份简历` },
    { id: 6, time: "昨天", text: `${feishuNeeds.length} 个招聘需求已提报` },
  ];
}
